<script setup lang="ts">
  import { ref, computed, useSlots, type VNode, provide } from 'vue'
  import { IonPage, IonContent } from '@ionic/vue'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import type { Swiper as SwiperType } from 'swiper'
  import { useEmployeeStore } from '@/stores/EmployeeStore'
  import { navigateToStartPage } from '@/router/actions/navigateToStartPage'
  import TriangleArrow from '@/components/TriangleArrow.vue'
  import PrimaryActionButton from '@/components/buttons/PrimaryActionButton.vue'
  import OnboardingVideoSlide from './OnboardingVideoSlide.vue'
  import 'swiper/css'
  import 'swiper/css/pagination'

  const props = withDefaults(
    defineProps<{
      finishButtonText?: string
    }>(),
    {
      finishButtonText: 'Finish',
    },
  )

  const emit = defineEmits<{
    finish: []
    skip: []
  }>()

  const { employee } = useEmployeeStore()

  const activeSlide = ref(0)
  const videoProgress = ref<Record<number, number>>({})
  const videoSlideRefs = ref<
    Record<number, InstanceType<typeof OnboardingVideoSlide>>
  >({})
  const swiperRef = ref()

  // Provide active slide to child components for automatic animation control
  provide('activeSlide', activeSlide)

  const slots = useSlots()

  const normalizedChildren = computed(() => {
    return slots.default?.() || []
  })

  const totalSlides = computed(() => normalizedChildren.value.length)
  const onSlideChange = (swiper: SwiperType) => {
    const newSlide = swiper.activeIndex
    activeSlide.value = newSlide
  }

  const onVideoProgress = (slideIndex: number, progress: number) => {
    videoProgress.value[slideIndex] = progress
  }

  const onVideoCompleted = (slideIndex: number) => {
    console.log('Video completed on slide:', slideIndex)
    // Advance to next slide when video completes
    if (swiperRef.value && activeSlide.value === slideIndex) {
      swiperRef.value.$el.swiper.slideNext()
    }
  }

  const onVideoError = (_slideIndex: number, error: Event) => {
    console.error('Video error:', error)
  }

  const goToNextSlide = () => {
    if (swiperRef.value && activeSlide.value < totalSlides.value - 1) {
      swiperRef.value.$el.swiper.slideNext()
    }
  }

  const goToPreviousSlide = () => {
    if (swiperRef.value && activeSlide.value > 0) {
      swiperRef.value.$el.swiper.slidePrev()
    }
  }

  const onFinishClick = () => {
    emit('finish')
  }

  const onSkipClick = () => {
    emit('skip')
  }

  if (!employee) {
    throw new Error('Can not continue onboarding without employee')
  }

  if (!employee.can_use_time_tracking) {
    void navigateToStartPage()
  }

  function isVideoSlide(vnode: VNode) {
    return vnode.type === OnboardingVideoSlide
  }
</script>
<template>
  <ion-page class="h-screen overflow-y-auto">
    <ion-content>
      <div
        class="onboarding-container safe-area-padding-top safe-area-padding-bottom"
      >
        <!-- Header Bar -->
        <div class="header-bar font-dmsans">
          <button
            class="header-button back-button"
            :class="{ hidden: activeSlide === 0 }"
            @click="goToPreviousSlide"
          >
            <triangle-arrow direction="left" />
            {{ $t('words.back') }}
          </button>
          <button
            class="header-button skip-button"
            :class="{ hidden: activeSlide === totalSlides - 1 }"
            @click="onSkipClick"
          >
            {{ $t('words.skip') }}
          </button>
        </div>

        <swiper
          ref="swiperRef"
          :slides-per-view="1"
          :space-between="0"
          :pagination="false"
          class="swiper-container"
          @slide-change="onSlideChange"
        >
          <template v-for="(vnode, i) in normalizedChildren" :key="i">
            <swiper-slide :data-video="isVideoSlide(vnode)">
              <component
                :is="vnode"
                v-if="isVideoSlide(vnode)"
                :ref="
                  (el: any) => {
                    if (el) videoSlideRefs[i] = el
                  }
                "
                :slide-index="i"
                @progress="(progress: number) => onVideoProgress(i, progress)"
                @completed="() => onVideoCompleted(i)"
                @error="(error: Event) => onVideoError(i, error)"
              />
              <component :is="vnode" v-else :slide-index="i" />
            </swiper-slide>
          </template>
        </swiper>

        <!-- Pagination Container -->
        <div class="pagination-container font-dmsans text-clockin-text">
          <!-- Finish Button (overlays dots on last slide) -->
          <transition name="finish-button-fade" mode="out-in">
            <div v-if="activeSlide === totalSlides - 1" class="finish-button">
              <primary-action-button @click="onFinishClick">
                {{ props.finishButtonText }}
              </primary-action-button>
            </div>
          </transition>

          <!-- Custom Pagination (hidden when finish button is shown) -->
          <div
            class="custom-pagination"
            :class="{ hidden: activeSlide === totalSlides - 1 }"
          >
            <template v-for="(vnode, index) in normalizedChildren" :key="index">
              <!-- Video slide: Progress Bar -->
              <div
                v-if="isVideoSlide(vnode)"
                class="pagination-dot"
                :class="{
                  active: activeSlide === index,
                  'progress-bar': activeSlide === index,
                }"
              >
                <div
                  v-if="activeSlide === index"
                  class="progress-fill"
                  :style="{
                    width: `${videoProgress[index] || 0}%`,
                  }"
                ></div>
              </div>

              <!-- Regular slide: Dot -->
              <div
                v-else
                class="pagination-dot"
                :class="{ active: activeSlide === index }"
              ></div>
            </template>
          </div>

          <!-- Next Button -->
          <button
            class="next-button"
            :class="{ hidden: activeSlide === totalSlides - 1 }"
            @click="goToNextSlide"
          >
            {{ $t('words.next_button') }}
            <triangle-arrow direction="right" />
          </button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
  ion-content {
    --overflow: hidden;
  }

  /* Header Bar Styling */
  .header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    flex-shrink: 0;
  }

  .header-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    color: #333;
    border: none;
    padding: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .header-button:hover:not(.hidden) {
    opacity: 0.7;
  }

  .header-button.hidden {
    opacity: 0;
    transform: translateX(-20px);
    pointer-events: none;
  }

  .back-button {
    justify-content: flex-start;
  }

  .skip-button {
    justify-content: flex-end;
  }

  .onboarding-container {
    display: flex;
    flex-direction: column;
    height: 100vh;
  }

  .swiper-container {
    flex: 1;
    width: 100%;
  }

  .slide-container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 100%;
    width: 100%;
    padding: 0 20px;
    box-sizing: border-box;
  }

  /* Pagination Container */
  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    flex-shrink: 0;
    position: relative;
  }

  /* Custom Pagination Styling */
  .custom-pagination {
    position: relative;
    display: flex;
    gap: 8px;
    width: calc(50% + v-bind(totalSlides) * (12px + 8px) / 2);
    justify-content: right;
    flex-grow: 0;
  }

  .pagination-dot {
    height: 12px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    flex: 1;
    position: relative;
    overflow: hidden;
  }

  .pagination-dot.active {
    background: rgba(0, 0, 0, 0.4);
  }

  .pagination-dot.progress-bar {
    background: rgba(0, 0, 0, 0.1);
  }

  .progress-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: currentColor;
    border-radius: 6px;
    padding-left: 12px;
  }

  /* Regular dots for slides 2 and 3 */
  .pagination-dot:not(.progress-bar) {
    flex: 0 0 12px;
  }

  .pagination-dot:not(.progress-bar).active {
    background: currentColor;
  }

  /* Next Button Styling */
  .next-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    color: currentColor;
    border: none;
    border-radius: 8px;
    padding: 0;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 1;
    transform: translateX(0);
    z-index: 1;
  }

  .next-button.hidden {
    opacity: 0;
    transform: translateX(20px);
    pointer-events: none;
  }

  .next-button:hover:not(.hidden) {
    opacity: 0.8;
    transform: translateX(2px);
  }

  /* Finish Button Positioning (PrimaryActionButton provides base styling) */
  .finish-button {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% - 20px * 2);
    flex: none;
    opacity: 1;
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
    z-index: 2;
  }

  /* Finish Button Transition */
  .finish-button-fade-enter-active,
  .finish-button-fade-leave-active {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }

  .finish-button-fade-enter-from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }

  .finish-button-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }

  /* Hide pagination when finish button is shown */
  .custom-pagination.hidden {
    opacity: 0;
    pointer-events: none;
  }

  /* Remove default Swiper pagination */
  :deep(.swiper-pagination) {
    display: none;
  }
</style>
